"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { Maximize2, Minimize2 } from "lucide-react";
import { fetcher } from "@/lib/api";
import { isKkiDay, STRONG_VALUE, STRONG_BODY, EOK } from "@/lib/kki";
import type { ApiResponse, ChartCandle, StageRecord } from "@/types";
import type { Time, MouseEventParams } from "lightweight-charts";

interface StockChartProps {
  portfolioName: string;
  stockCode: string;
  stockName: string;
  buyStages?: StageRecord[];
  /** 차트 데이터 소스 URL 베이스 오버라이드 (예: ETF(us)는 fdr 엔드포인트). period 는 자동 append. */
  chartUrlBase?: string;
  /** 초기 화면에 보여줄 봉 수 (기본 200). 데이터가 이보다 적거나 같으면 전체 표시. */
  initialBars?: number;
  /** 이동평균선 목록 (기본 DEFAULT_MA_LINES). 모듈 상수로 넘길 것 — 인라인 배열은 차트 재생성 유발. */
  maLines?: MaLine[];
  /** 표시할 기간 버튼 (기본 5분/일/주/월 전체) */
  periods?: readonly Period[];
  /** VWAP 체크박스 노출 — 체크 시 이평선 대신 VWAP 선 표시 (지수 차트용) */
  enableVwap?: boolean;
  /** 거래대금 표기 통화 (기본 KRW). KRW=조/억, USD=$B/$M. */
  amountCurrency?: "KRW" | "USD";
  /**
   * 하단 히스토그램을 거래량 대신 거래대금으로 그린다 (지수 차트용).
   * 캔들에 amount 가 없는 심볼(나스닥·S&P500 등 지수)은 거래량으로 자동 폴백한다.
   */
  amountHistogram?: boolean;
  /** 끼 밴드(노란 세로선) 표시 — 기본 켜짐. 종목 개념이 아닌 지수 차트에서는 끈다. */
  kkiBand?: boolean;
  /** 볼린저 밴드 체크박스 노출 (모든 차트 기본 노출). 노출 시 기본 켜짐. */
  enableBollinger?: boolean;
  /**
   * 하단 amount 가 부호 있는 순매수 금액일 때의 라벨 (예: "외국인 순매수").
   * 지정 시 막대 색은 금액 부호(+빨강 / −파랑), 툴팁도 이 라벨로 표시한다.
   */
  amountLabel?: string;
}

const STAGE_COLOR = "#ffcc00"; // 노란색
const VWAP_COLOR = "#0891b2";  // VWAP 선 색 (cyan-700)
const KKI_BAND_COLOR = "rgba(250,204,21,0.55)"; // 끼 밴드 — 끼 화면 차트와 같은 노랑
const BB_COLOR = "#c8c8c8";    // 볼린저 밴드 — 연한 회색
const BB_PERIOD = 20;          // 볼린저 기간 (중심선 = 20일 이평 = MA20)
const BB_K = 2.33;             // 표준편차 배수
// MA20 이격 밴드 — |종가/MA20−1| ≥ 25% 또는 |종가−MA20| ≥ 5×ATR(14) 인 일봉. 그날 BB 에 닿았으면 오렌지.
const FAR_MA = 20;
const FAR_PCT = 0.25;
const FAR_ATR = 5;
const ATR_N = 14;
const FAR_GOLD = "rgba(255,215,0,0.35)";
const FAR_ORANGE = "rgba(249,115,22,0.35)";
type BandMode = "kki" | "ma20";

// 이동평균선 설정 — 20일선은 굵은 오렌지로 강조 (maLines prop 으로 교체 가능)
export type MaLine = { period: number; color: string; lineWidth: number };
export const DEFAULT_MA_LINES: MaLine[] = [
  { period: 5, color: "#ec4899", lineWidth: 1 },   // 핑크
  { period: 20, color: "#f97316", lineWidth: 3 },  // 굵은 오렌지
  { period: 60, color: "#16a34a", lineWidth: 1 },  // 초록
  { period: 120, color: "#8b5cf6", lineWidth: 1 }, // 보라
];

const ALL_PERIODS = ["5m", "D", "W", "M"] as const;
type Period = "1m" | (typeof ALL_PERIODS)[number];
// 분봉 여부 — 국내 5분봉 / 해외 1분봉
const isIntraday = (p: Period) => p === "1m" || p === "5m";

// 하이킨아시 (2026-10-08 · eztrade detail.js haBar 와 같은 식) — **분봉에서만**, 기본은 꺼짐.
// 잔파동을 지워 추세만 남긴다. 캔들 **모양만** 바꾼다 — 이평선·VWAP·볼린저·매수 차수선·거래량은
// 원본 가격 그대로다(실제 주문이 걸리는 자리라 합성 가격으로 옮기면 안 된다).
const HA_KEY = "ezsplit-ha";
type HaBar = { time: ChartCandle["time"]; open: number; high: number; low: number; close: number };
function haBar(c: ChartCandle, prev?: HaBar): HaBar {
  const close = (c.open + c.high + c.low + c.close) / 4;
  const open = prev ? (prev.open + prev.close) / 2 : (c.open + c.close) / 2;
  return { time: c.time, open, close, high: Math.max(c.high, open, close), low: Math.min(c.low, open, close) };
}
function haSeries(candles: ChartCandle[]): HaBar[] {
  const out: HaBar[] = [];
  for (const c of candles) out.push(haBar(c, out[out.length - 1]));
  return out;
}

// 단순이동평균 — n-1번째 봉까지는 값 없음
function sma(candles: { time: string | number; close: number }[], n: number) {
  const out: { time: Time; value: number }[] = [];
  let sum = 0;
  for (let i = 0; i < candles.length; i++) {
    sum += candles[i].close;
    if (i >= n) sum -= candles[i - n].close;
    if (i >= n - 1) out.push({ time: candles[i].time as Time, value: sum / n });
  }
  return out;
}

// 볼린저 밴드 — 중심선 SMA(n), 상·하단 = 중심선 ± k×표준편차(모집단). n-1번째 봉까지는 값 없음.
function bollinger(candles: { time: string | number; close: number }[], n: number, k: number) {
  const upper: { time: Time; value: number }[] = [];
  const middle: { time: Time; value: number }[] = [];
  const lower: { time: Time; value: number }[] = [];
  for (let i = n - 1; i < candles.length; i++) {
    let sum = 0;
    for (let j = i - n + 1; j <= i; j++) sum += candles[j].close;
    const mean = sum / n;
    let varSum = 0;
    for (let j = i - n + 1; j <= i; j++) varSum += (candles[j].close - mean) ** 2;
    const sd = Math.sqrt(varSum / n);
    const t = candles[i].time as Time;
    upper.push({ time: t, value: mean + k * sd });
    middle.push({ time: t, value: mean });
    lower.push({ time: t, value: mean - k * sd });
  }
  return { upper, middle, lower };
}

// ATR — Wilder 방식. 첫 n봉은 TR 단순평균, 이후 (이전 ATR×(n−1) + TR) / n. n-1번째 봉까지는 null.
function wilderAtr(candles: ChartCandle[], n: number): (number | null)[] {
  const out: (number | null)[] = [];
  let atr = 0, trSum = 0;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const pc = i > 0 ? candles[i - 1].close : null;
    const tr = pc == null ? c.high - c.low : Math.max(c.high - c.low, Math.abs(c.high - pc), Math.abs(c.low - pc));
    if (i < n) {
      trSum += tr;
      if (i === n - 1) atr = trSum / n;
    } else {
      atr = (atr * (n - 1) + tr) / n;
    }
    out.push(i >= n - 1 ? atr : null);
  }
  return out;
}

// 누적 VWAP — typical=(H+L+C)/3 을 거래량 가중 누적평균. 거래량 0(지수·FRED 등)이면 typical 누적평균으로 폴백.
function vwapSeries(candles: ChartCandle[]) {
  const out: { time: Time; value: number }[] = [];
  let pv = 0, vv = 0, tSum = 0;
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const typical = (c.high + c.low + c.close) / 3;
    const vol = c.volume > 0 ? c.volume : 0;
    pv += typical * vol; vv += vol; tSum += typical;
    out.push({ time: c.time as Time, value: vv > 0 ? pv / vv : tSum / (i + 1) });
  }
  return out;
}

// 거래대금 표기 — 원화는 조/억, 달러는 $B/$M (예: 23.1조, $594M)
function fmtAmount(v: number, currency: "KRW" | "USD"): string {
  if (v < 0) return `-${fmtAmount(-v, currency)}`;
  if (currency === "KRW") {
    if (v >= 1e12) return `${(v / 1e12).toFixed(1)}조`;
    if (v >= 1e8) return `${Math.round(v / 1e8).toLocaleString()}억`;
    return `${Math.round(v).toLocaleString()}원`;
  }
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${Math.round(v / 1e6).toLocaleString()}M`;
  if (v >= 1e3) return `$${Math.round(v / 1e3).toLocaleString()}K`;
  return `$${Math.round(v).toLocaleString()}`;
}

export function StockChart({ portfolioName, stockCode, stockName, buyStages = [], chartUrlBase, initialBars = 200, maLines = DEFAULT_MA_LINES, periods = ALL_PERIODS, enableVwap = false, amountCurrency = "KRW", amountHistogram = false, kkiBand = true, enableBollinger = true, amountLabel }: StockChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReturnType<typeof import("lightweight-charts").createChart> | null>(null);
  const [period, setPeriod] = useState<Period>("D");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [vwapOn, setVwapOn] = useState(false);  // VWAP 체크 시 이평선 대신 VWAP 선
  const showVwap = enableVwap && vwapOn;
  const [bbOn, setBbOn] = useState(true);       // 볼린저 밴드 (노출 시 기본 켜짐)
  const showBb = enableBollinger && bbOn;
  // 하이킨아시 — 브라우저에 남겨 다시 열어도 유지한다. localStorage 는 서버 렌더에 없어 마운트 뒤 읽는다
  const [haOn, setHaOn] = useState(false);
  useEffect(() => { setHaOn(window.localStorage.getItem(HA_KEY) === "on"); }, []);
  const toggleHa = (on: boolean) => {
    setHaOn(on);
    window.localStorage.setItem(HA_KEY, on ? "on" : "off");
  };
  const showHa = haOn && isIntraday(period);
  // 해외(USD)는 KIS 해외 분봉 API 가 1분 단위 — 5분 버튼 자리를 1분으로 바꾼다
  const periodList = useMemo<Period[]>(
    () => periods.map((p) => (p === "5m" && amountCurrency === "USD" ? "1m" : p)),
    [periods, amountCurrency]
  );

  // 전체 화면일 때 Esc 로 닫기.
  // capture + stopImmediatePropagation — 바깥(지표 드랍다운 등)의 Esc 핸들러가 같이 닫히지 않게 한다.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      setIsFullscreen(false);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isFullscreen]);

  const { data } = useSWR<ApiResponse<ChartCandle[]>>(
    stockCode
      ? (chartUrlBase
          ? `${chartUrlBase}&period=${period}`
          : `/api/portfolio/chart?name=${encodeURIComponent(portfolioName)}&code=${encodeURIComponent(stockCode)}&period=${period}`)
      : null,
    fetcher
  );

  const candles = data?.data || [];
  // 캔들에 거래대금이 실려 있는지 — 없으면 툴팁·히스토그램 모두 거래량으로 폴백
  const hasAmount = candles.some((c) => c.amount != null);
  // 히스토그램을 거래대금으로 그릴지 (지수 차트만)
  const useAmount = amountHistogram && hasAmount;
  // 세로 띠 모드 — 끼 / MA20 이격. 일봉에서만.
  const [bandMode, setBandMode] = useState<BandMode>("ma20");
  const showBandToggle = kkiBand && period === "D";
  // 끼 밴드 — 원화 거래대금이 있는 일봉에서만 (끼는 국내 일봉 개념)
  const showKki = showBandToggle && bandMode === "kki" && amountCurrency === "KRW" && hasAmount;
  const showFar = showBandToggle && bandMode === "ma20";

  useEffect(() => {
    if (!chartContainerRef.current || candles.length === 0) return;

    let isMounted = true;

    (async () => {
      const { createChart, CandlestickSeries, HistogramSeries, LineSeries, ColorType } = await import("lightweight-charts");

      if (!isMounted || !chartContainerRef.current) return;

      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }

      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight,
        layout: {
          background: { type: ColorType.Solid, color: "#ffffff" },
          textColor: "rgba(0,0,0,0.55)",
          fontSize: 12,
        },
        grid: {
          vertLines: { color: "rgba(0,0,0,0.06)" },
          horzLines: { color: "rgba(0,0,0,0.06)" },
        },
        crosshair: {
          mode: 0,
        },
        rightPriceScale: {
          borderColor: "rgba(0,0,0,0.12)",
        },
        timeScale: {
          borderColor: "rgba(0,0,0,0.12)",
          timeVisible: isIntraday(period),
          secondsVisible: false,
          tickMarkFormatter: isIntraday(period)
            ? (time: number) => {
                const d = new Date(time * 1000);
                const hh = String(d.getHours()).padStart(2, "0");
                const mm = String(d.getMinutes()).padStart(2, "0");
                return `${hh}:${mm}`;
              }
            : undefined,
        },
        localization: isIntraday(period)
          ? {
              timeFormatter: (time: number) => {
                const d = new Date(time * 1000);
                const yyyy = d.getFullYear();
                const mo = String(d.getMonth() + 1).padStart(2, "0");
                const dd = String(d.getDate()).padStart(2, "0");
                const hh = String(d.getHours()).padStart(2, "0");
                const mm = String(d.getMinutes()).padStart(2, "0");
                return `${yyyy}-${mo}-${dd} ${hh}:${mm}`;
              },
            }
          : undefined,
      });

      chartRef.current = chart;

      // 끼 밴드 — 캔들보다 먼저 그려야 뒤에 깔린다. 차트 높이를 꽉 채우는 노란 세로선.
      if (showKki) {
        const bandSeries = chart.addSeries(HistogramSeries, {
          priceScaleId: "kkiband",
          priceLineVisible: false,
          lastValueVisible: false,
        });
        chart.priceScale("kkiband").applyOptions({ scaleMargins: { top: 0, bottom: 0 } });
        bandSeries.setData(candles.map((c) => ({
          time: c.time,
          value: isKkiDay(c.open, c.close, c.amount ?? 0) ? 1 : 0,
          color: KKI_BAND_COLOR,
        })));
      }

      // MA20 이격 밴드 — 실제 가격(하이킨아시 무관)으로 계산. 캔들보다 먼저 그려 뒤에 깐다.
      // farInfo 는 툴팁 윗줄(이격률·ATR 배수)에도 쓴다.
      const farInfo = new Map<string, { pct: number; atrX: number | null }>();
      if (showFar) {
        const ma = sma(candles, FAR_MA);
        const maOff = candles.length - ma.length;
        const atr = wilderAtr(candles, ATR_N);
        const bb = candles.length >= BB_PERIOD ? bollinger(candles, BB_PERIOD, BB_K) : null;
        const bbOff = BB_PERIOD - 1; // bollinger() 결과는 BB_PERIOD-1 번째 봉부터 시작
        const bandSeries = chart.addSeries(HistogramSeries, {
          priceScaleId: "farband",
          priceLineVisible: false,
          lastValueVisible: false,
        });
        chart.priceScale("farband").applyOptions({ scaleMargins: { top: 0, bottom: 0 } });
        bandSeries.setData(candles.map((c, i) => {
          const m = i >= maOff ? ma[i - maOff].value : null;
          if (m == null || m <= 0) return { time: c.time, value: 0, color: FAR_GOLD };
          const pct = c.close / m - 1;
          const a = atr[i];
          const atrX = a != null && a > 0 ? (c.close - m) / a : null;
          farInfo.set(String(c.time), { pct, atrX });
          const far = Math.abs(pct) >= FAR_PCT || (atrX != null && Math.abs(atrX) >= FAR_ATR);
          const j = i - bbOff;
          const touch = bb != null && j >= 0 && (c.high >= bb.upper[j].value || c.low <= bb.lower[j].value);
          return { time: c.time, value: far ? 1 : 0, color: touch ? FAR_ORANGE : FAR_GOLD };
        }));
      }

      // Candlestick
      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: "#ef4444",
        downColor: "#3b82f6",
        borderUpColor: "#ef4444",
        borderDownColor: "#3b82f6",
        wickUpColor: "#ef4444",
        wickDownColor: "#3b82f6",
      });

      candleSeries.setData((showHa ? haSeries(candles) : candles).map((c) => ({
        time: c.time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })));

      // 매수 차수별 가격 라인 (중복 stage 제거)
      // 기본 title 은 차트 안쪽(캔들 위)에 그려져 우측이 가려지므로 쓰지 않고,
      // 우측 가격축 위에 '가격 N차' HTML 오버레이 라벨을 직접 그린다.
      const uniqueStages = buyStages.filter((s, i, arr) => arr.findIndex((x) => x.stage === s.stage) === i);
      const fmtLabelPrice = (v: number) => (v < 1 ? v.toFixed(4) : v.toFixed(2));
      const stageLabels: { el: HTMLDivElement; price: number }[] = [];
      for (const stage of uniqueStages) {
        if (stage.buy_price > 0) {
          candleSeries.createPriceLine({
            price: stage.buy_price,
            color: STAGE_COLOR,
            lineWidth: 1,
            lineStyle: 2, // Dashed
            axisLabelVisible: false,
            title: "",
          });
          const el = document.createElement("div");
          el.dataset.stageLabel = "1";
          el.textContent = `${fmtLabelPrice(stage.buy_price)} ${stage.stage}차`;
          Object.assign(el.style, {
            position: "absolute",
            right: "0",
            display: "none",
            zIndex: "3",
            pointerEvents: "none",
            background: STAGE_COLOR,
            color: "#000000",
            fontSize: "10px",
            fontWeight: "600",
            padding: "1px 4px",
            borderRadius: "2px",
            whiteSpace: "nowrap",
          });
          chartContainerRef.current!.appendChild(el);
          stageLabels.push({ el, price: stage.buy_price });
        }
      }
      const updateStageLabels = () => {
        for (const { el, price } of stageLabels) {
          const y = candleSeries.priceToCoordinate(price);
          if (y == null || y < 0) {
            el.style.display = "none";
            continue;
          }
          el.style.display = "block";
          el.style.top = `${Math.round(y - 8)}px`;
        }
      };
      chart.timeScale().subscribeVisibleLogicalRangeChange(updateStageLabels);

      if (showVwap) {
        // VWAP 선 (이평선 대체)
        const line = chart.addSeries(LineSeries, {
          color: VWAP_COLOR,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        line.setData(vwapSeries(candles));
      } else {
        // 이동평균선
        for (const ma of maLines) {
          const line = chart.addSeries(LineSeries, {
            color: ma.color,
            lineWidth: ma.lineWidth as 1 | 2 | 3 | 4,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });
          line.setData(sma(candles, ma.period));
        }
      }

      // 볼린저 밴드 — 연한 회색 점선. 상·하단만 그린다. 중심선은 MA20 과 같은 값이라,
      // MA20 이 화면에 없을 때(VWAP 모드 등)만 보강한다.
      if (showBb && candles.length >= BB_PERIOD) {
        const bb = bollinger(candles, BB_PERIOD, BB_K);
        const bbLine = (data: { time: Time; value: number }[]) => {
          const line = chart.addSeries(LineSeries, {
            color: BB_COLOR,
            lineWidth: 1,
            lineStyle: 2,   // 2=Dashed
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });
          line.setData(data);
        };
        bbLine(bb.upper);
        bbLine(bb.lower);
        if (showVwap || !maLines.some((ma) => ma.period === BB_PERIOD)) bbLine(bb.middle);
      }

      // 하단 히스토그램 — 거래대금(amountCurrency 지정 시) 또는 거래량
      const volumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: useAmount
          ? { type: "custom", formatter: (v: number) => fmtAmount(v, amountCurrency!), minMove: 1 }
          : { type: "volume" },
        priceScaleId: "volume",
      });

      chart.priceScale("volume").applyOptions({
        scaleMargins: { top: 0.85, bottom: 0 },
      });

      volumeSeries.setData(candles.map((c) => ({
        time: c.time,
        value: useAmount ? c.amount ?? 0 : c.volume,
        color: (amountLabel ? (c.amount ?? 0) >= 0 : c.close >= c.open) ? "rgba(239,68,68,0.3)" : "rgba(59,130,246,0.3)",
      })));

      // 크로스헤어 툴팁 — 차트 어디든 마우스 오버 시 해당 날짜의 종가·상승률·거래대금 표시
      const tip = document.createElement("div");
      tip.dataset.volTip = "1";
      Object.assign(tip.style, {
        position: "absolute",
        display: "none",
        zIndex: "4",
        pointerEvents: "none",
        background: "rgba(64,64,64,0.92)",
        color: "#ffffff",
        fontSize: "11px",
        lineHeight: "1.5",
        padding: "4px 7px",
        borderRadius: "4px",
        whiteSpace: "nowrap",
      });
      chartContainerRef.current.appendChild(tip);

      // 상승률은 직전 봉 종가 대비 — 첫 봉은 시가 대비로 대체
      const byTime = new Map(
        candles.map((c, i) => [String(c.time), { c, base: i > 0 ? candles[i - 1].close : c.open }])
      );
      const onCrosshair = (param: MouseEventParams) => {
        const box = chartContainerRef.current;
        const hit = param.time != null ? byTime.get(String(param.time)) : undefined;
        if (!box || !param.point || !hit) {
          tip.style.display = "none";
          return;
        }
        const { c, base } = hit;
        const rate = base > 0 ? ((c.close - base) / base) * 100 : 0;
        const fi = farInfo.get(String(c.time));
        tip.innerHTML = [
          fi
            ? `<div style="border-bottom:1px dashed rgba(255,255,255,0.5);margin-bottom:2px;padding-bottom:2px">MA${FAR_MA} ${fi.pct >= 0 ? "+" : ""}${(fi.pct * 100).toFixed(1)}%${fi.atrX != null ? ` (${Math.abs(fi.atrX).toFixed(1)} ATR)` : ""}</div>`
            : "",
          `<div style="opacity:0.75">${String(c.time)}</div>`,
          `<div>종가 ${c.close.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>`,
          `<div>상승률 ${rate >= 0 ? "+" : ""}${rate.toFixed(2)}%</div>`,
          amountLabel
            ? `<div>${amountLabel} ${c.amount != null ? fmtAmount(c.amount, amountCurrency) : "-"}</div>`
            : c.amount != null
            ? `<div>거래대금 ${fmtAmount(c.amount, amountCurrency)}</div>`
            : `<div>거래량 ${c.volume.toLocaleString()}</div>`,
        ].join("");
        tip.style.display = "block";
        tip.style.left = `${Math.max(4, Math.min(param.point.x + 14, box.clientWidth - tip.offsetWidth - 4))}px`;
        tip.style.top = `${Math.max(4, Math.min(param.point.y + 14, box.clientHeight - tip.offsetHeight - 4))}px`;
      };
      chart.subscribeCrosshairMove(onCrosshair);

      // 초기 화면은 최근 initialBars 봉만 표시 — 이전 봉은 왼쪽 스크롤로 확인
      if (candles.length > initialBars) {
        chart.timeScale().setVisibleLogicalRange({ from: candles.length - initialBars, to: candles.length });
      } else {
        chart.timeScale().fitContent();
      }
      requestAnimationFrame(updateStageLabels);

      const ro = new ResizeObserver(() => {
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
          });
          requestAnimationFrame(updateStageLabels);
        }
      });
      ro.observe(chartContainerRef.current);

      return () => {
        ro.disconnect();
        for (const { el } of stageLabels) el.remove();
        chart.unsubscribeCrosshairMove(onCrosshair);
        tip.remove();
      };
    })();

    return () => {
      isMounted = false;
      // 차수 라벨·툴팁 오버레이 제거 (async 내부 return 은 React 클린업으로 호출되지 않음)
      chartContainerRef.current?.querySelectorAll("[data-stage-label],[data-vol-tip]").forEach((n) => n.remove());
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [candles, buyStages, initialBars, maLines, showVwap, useAmount, amountCurrency, showKki, showFar, showBb, showHa, amountLabel]);

  return (
    <div
      className={
        isFullscreen
          ? "fixed inset-0 z-[100] p-4 flex flex-col"
          : "rounded-lg p-4 h-full flex flex-col"
      }
      style={{ background: "#ffffff" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-neutral-900">{stockName}</span>
          <span className="text-xs text-black/55">{stockCode}</span>
          {/* 차수 범례 */}
          {buyStages.filter((s) => s.buy_price > 0).map((s, i) => (
            <span
              key={`${s.stage}-${i}`}
              className="text-xs px-1.5 py-0.5 rounded font-semibold border"
              style={{ color: STAGE_COLOR, background: "rgba(255,140,0,0.18)", borderColor: "rgba(255,140,0,0.45)" }}
            >
              {s.stage}차
            </span>
          ))}
          {/* 이동평균 / VWAP 범례 */}
          <span className="flex items-center gap-1.5 ml-1">
            {showVwap ? (
              <span className="text-2xs" style={{ color: VWAP_COLOR, fontWeight: 700 }}>VWAP</span>
            ) : (
              maLines.map((ma) => (
                <span key={ma.period} className="text-2xs" style={{ color: ma.color, fontWeight: ma.lineWidth > 1 ? 700 : 400 }}>
                  {ma.period}
                </span>
              ))
            )}
          </span>
          {/* VWAP 토글 (지수 차트) — 체크 시 이평선 대신 VWAP 선 */}
          {enableVwap && (
            <label className="flex items-center gap-1 text-2xs text-black/60 cursor-pointer select-none ml-1">
              <input
                type="checkbox"
                checked={vwapOn}
                onChange={(e) => setVwapOn(e.target.checked)}
                className="cursor-pointer"
              />
              VWAP
            </label>
          )}
          {/* 볼린저 밴드 토글 (지수 차트) — 중심선 MA20 ± 2.33σ */}
          {enableBollinger && (
            <label
              className="flex items-center gap-1 text-2xs cursor-pointer select-none ml-1"
              style={{ color: "rgba(0,0,0,0.6)", fontWeight: showBb ? 700 : 400 }}
              title={`볼린저 밴드 — ${BB_PERIOD}일 이평 ± 표준편차×${BB_K} (중심선은 MA${BB_PERIOD})`}
            >
              <input
                type="checkbox"
                checked={bbOn}
                onChange={(e) => setBbOn(e.target.checked)}
                className="cursor-pointer"
              />
              BB({BB_PERIOD}, {BB_K})
            </label>
          )}
          {/* 세로 띠 토글 (일봉) — 누를 때마다 끼 ↔ MA20이격 */}
          {showBandToggle && (
            <button
              type="button"
              onClick={() => setBandMode((m) => (m === "kki" ? "ma20" : "kki"))}
              className="text-2xs px-1.5 py-0.5 rounded font-semibold ml-1 cursor-pointer"
              style={{ background: bandMode === "kki" ? KKI_BAND_COLOR : FAR_GOLD, color: "#000000" }}
              title={bandMode === "kki"
                ? `끼 — 거래대금 ${EOK(STRONG_VALUE)}↑ 이면서 시가 대비 종가 ${STRONG_BODY}%↑ 인 날\n누르면 MA${FAR_MA}이격으로 바뀝니다`
                : `MA${FAR_MA}이격 — |종가/MA${FAR_MA}−1| ≥ ${FAR_PCT * 100}% 또는 |종가−MA${FAR_MA}| ≥ ${FAR_ATR}×ATR(${ATR_N}) 인 날 (골드)\n그날 BB(${BB_PERIOD}, ${BB_K})에 닿았으면 오렌지\n누르면 끼로 바뀝니다`}
            >
              {bandMode === "kki" ? "끼" : `MA${FAR_MA}이격`}
            </button>
          )}
          {/* 하이킨아시 토글 — 분봉에서만 보인다 (eztrade 와 같은 규칙) */}
          {isIntraday(period) && (
            <label
              className="flex items-center gap-1 text-2xs cursor-pointer select-none ml-1"
              style={{ color: showHa ? "#0f766e" : "rgba(0,0,0,0.6)", fontWeight: showHa ? 700 : 400 }}
              title="하이킨아시 — 잔파동을 지워 추세만 남긴 합성 캔들. 캔들 모양만 바뀝니다 (이평선·차수선·거래량은 실제 가격 그대로)"
            >
              <input
                type="checkbox"
                checked={haOn}
                onChange={(e) => toggleHa(e.target.checked)}
                className="cursor-pointer"
              />
              HA
            </label>
          )}
        </div>
        <div className="flex gap-1">
          {periodList.map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors border ${
                period === p
                  ? "bg-blue-600 border-blue-600 text-white font-semibold"
                  : "bg-white border-black/[0.2] text-black/80 hover:text-neutral-900 hover:border-black/[0.35]"
              }`}
            >
              {p === "5m" ? "5분" : p === "D" ? "일봉" : p === "W" ? "주봉" : "월봉"}
            </button>
          ))}
          {/* 전체 화면 토글 */}
          <button
            onClick={() => setIsFullscreen((v) => !v)}
            className="px-2 py-1 rounded-md border bg-white border-black/[0.2] text-black/80 hover:text-neutral-900 hover:border-black/[0.35] transition-colors"
            title={isFullscreen ? "전체 화면 닫기 (Esc)" : "전체 화면"}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        </div>
      </div>
      {/* Chart */}
      <div ref={chartContainerRef} className="relative flex-1 min-h-0" />
      {candles.length === 0 && (
        <div className="flex items-center justify-center h-full">
          <p className="text-xs text-black/45">차트 데이터 로딩중...</p>
        </div>
      )}
    </div>
  );
}
